import React from "react";
import appContext from "../../appContext";
import clockIcon from "../../Assets/clock-icon.png";

import { useNavigate } from "react-router-dom";
import { useCallback, useContext, useEffect, useMemo, useState, useRef } from "react";
import { TimeEstimateContainer, TimeTakenContainer } from "./Styled";
import { verifyRecaptchaV2, verifyRecaptchaV3 } from "../../API/api";
import "./style.scss";
import ReCAPTCHA from "react-google-recaptcha";
import { Dialog, DialogContent, DialogTitle, Snackbar } from "@mui/material";
import { Alert } from "@mui/material";

function LandingPage() {
  const navigate = useNavigate();
  const { allContentData, landingPage, validId, timeEstimate } =
    useContext(appContext);
  const [open, setOpen] = useState(false)
  const [errMsg, setErrMsg] = useState("")
  const [showV2, setShowV2] = useState(false)
  const [isProcessing, setIsProcessing] = useState(false);
  const recaptchaRef = useRef(null);

  const handleV2Change = async (token) => {
    if (!token) return;
    try {
      const v2Res = await verifyRecaptchaV2(token);
      if (v2Res.status != 200 || !v2Res?.data?.data?.success) {
        setOpen(true);
        setErrMsg("ReCAPTCHA V2 verification failed.");
        return;
      }
      setShowV2(false);
      if (!validId) {
        navigate("/notfound");
      } else {
        navigate(`/form${window.location.search}`);
      }
    } catch (error) {
      console.error("V2 verification error:", error);
      setOpen(true);
      setErrMsg("V2 Error. Please try again.");
    }
  };

  const handleV3Change = async () => {
    try {
      if (!process.env.REACT_APP_RECAPTCHA_SITE_KEY_V3) {
        console.error("ReCAPTCHA Site Key V3 is missing.");
        throw new Error("Configuration error: ReCAPTCHA site key missing.");
      }

      // Wait to ensure ReCAPTCHA is initialized and script is loaded
      let retryCount = 0;
      // Specifically check for .execute to ensure the script is fully ready
      while ((!window.grecaptcha || !window.grecaptcha.execute) && retryCount < 10) {
        console.log(`Waiting for ReCAPTCHA... Attempt ${retryCount + 1}`);
        await new Promise(resolve => setTimeout(resolve, 500));
        retryCount++;
      }

      if (!window.grecaptcha || !window.grecaptcha.execute) {
        throw new Error("ReCAPTCHA script failed to load. Please check your connection or disable ad-blockers.");
      }

      if (recaptchaRef.current) {
        console.log("Executing ReCAPTCHA V3...");
        console.log("Current Domain:", window.location.hostname);
        console.log("Site Key (Start):", process.env.REACT_APP_RECAPTCHA_SITE_KEY_V3);
        
        // Add a safety timeout to the executeAsync itself
        const tokenPromise = recaptchaRef.current.executeAsync();
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("ReCAPTCHA execution timed out (10s).")), 10000)
        );

        const token = await Promise.race([tokenPromise, timeoutPromise]);

        if (!token) {
          throw new Error("ReCAPTCHA returned null token. Check site key or domain permissions in Google Admin Console.");
        }

        recaptchaRef.current.reset();
        const recaptchaRes = await verifyRecaptchaV3(token);
        if (recaptchaRes.status != 200) {
          setOpen(true);
          setErrMsg("ReCAPTCHA verification failed.");
          return false;
        }
        if (recaptchaRes?.data?.data?.score < 0.5) {
          setShowV2(true);
          return false;
        }
        return true;
      } else {
        throw new Error("ReCAPTCHA component not found.");
      }
    } catch (error) {
      console.error("ReCAPTCHA error:", error);
      setOpen(true);
      setErrMsg(`ReCAPTCHA error: ${error.message || "Please try again."}`);
      return false;
    }
  };

  useEffect(() => {
    const runChecks = async () => {
      if (landingPage !== null && !landingPage) {
        const success = await handleV3Change();
        if (success) navigate(`/form${window.location.search}`);
      } else if (!validId) {
        navigate("/notfound");
      }
    };
    runChecks();
  }, [landingPage, navigate, validId]);

  const getTimePhrase = (count, phrase) => {
    if (count === 0) {
      return "";
    } else if (count === 1) {
      return `${count} ${phrase}`;
    } else {
      return `${count} ${phrase}s`;
    }
  };

  const getApproxTime = useCallback(() => {
    const hours = Math.floor(timeEstimate / 3600);
    const minutes = Math.floor((timeEstimate % 3600) / 60);
    const seconds = timeEstimate % 60;

    if (hours) {
      const exactHours = getTimePhrase(hours, "hour");
      const exactMinutes = getTimePhrase(minutes, "minute");
      const exactSeconds = getTimePhrase(seconds, "second");

      return `${exactHours} ${exactMinutes} ${exactSeconds}`;
    }

    if (minutes) {
      const exactMinutes = getTimePhrase(minutes, "minute");
      const exactSeconds = getTimePhrase(seconds, "second");

      return `${exactMinutes} ${exactSeconds}`;
    }

    return getTimePhrase(seconds, "second");
  }, [timeEstimate]);

  const renderShowTimeComponent = useMemo(() => {
    if (timeEstimate) {
      return (
        <TimeEstimateContainer>
          <img src={clockIcon} alt="clock icon" />
          <TimeTakenContainer>{`Takes ${getApproxTime()}`}</TimeTakenContainer>
        </TimeEstimateContainer>
      );
    }

    return null;
  }, [timeEstimate, getApproxTime]);

  const renderHtmlOrCustomComponent = useMemo(() => {
    if (allContentData?.style?.landingPageContentType === "html") {
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: allContentData?.formContent || "",
          }}
        />
      );
    }

    if (allContentData?.style?.landingPageContentType === "custom") {
      return (
        <div className="landing-page-custom-text-container">
          {allContentData?.formContent}
        </div>
      );
    }

    return null;
  }, [allContentData]);

  const getIsHtmlFromTemplate = () => {
    return allContentData?.formContent?.includes("DOCTYPE");
  };

  const onStartClick = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const success = await handleV3Change();
      if (success) {
        navigate(`/form${window.location.search}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <React.Fragment>
      <div className="landing-container">
        {renderHtmlOrCustomComponent}
        <div
          className="time-and-start-button"
        >
          {renderShowTimeComponent}
          <button
            className="open-form-button button_styles"
            onClick={onStartClick}
            disabled={isProcessing}
          >
            {isProcessing ? "Validating..." : allContentData?.style?.landingPageButtonText}
          </button>
          <ReCAPTCHA
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY_V3}
            size="invisible"
            ref={recaptchaRef}
          />
        </div>
        <Snackbar
          open={open}
          autoHideDuration={3000}
          onClose={() => { setOpen(false); setErrMsg("") }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert onClose={() => { setOpen(false); setErrMsg("") }} severity="error">
            {errMsg}
          </Alert>
        </Snackbar>
        <Dialog open={showV2} onClose={() => setShowV2(false)}>
          <DialogTitle>Please verify you are a human</DialogTitle>
          <DialogContent style={{ display: 'flex', justifyContent: 'center', paddingTop: '10px' }}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY_V2}
              onChange={handleV2Change}
            />
          </DialogContent>
        </Dialog>
      </div>
    </React.Fragment>
  );
}

export default LandingPage;
